/**
 * Single source of truth for form-field conditional visibility.
 *
 * Visibility is driven entirely by each field's `visibility_rules`, which the
 * backend copies from the master field config (com_fields.visibility_rules)
 * into every form-template field snapshot — and now also re-syncs on read, so
 * a rule configured in field config flows to existing forms without a re-save.
 *
 * Field configuration remains authoritative BY DEFAULT: a rule set there
 * applies wherever the field is used. A form may deliberately depart from it via
 * `visibility_rules_override` — see effectiveRules below — which the backend
 * also resolves into `visibility_rules` on read. Both are honored here so the
 * form builder's live preview is right the moment an author changes a rule,
 * before any save round-trip. The member edit form, form-builder
 * preview, live instance editor, and public intake pages all evaluate the same
 * rules through this module so behavior is identical everywhere. (The legacy
 * `conditional_logic` field is deprecated and no longer evaluated.)
 */

export interface VisibilityCondition {
  field: string;
  operator: string;
  value?: any;
}

export interface VisibilityRules {
  logic?: 'AND' | 'OR';
  conditions?: VisibilityCondition[];
  when_hidden?: 'hide' | 'disable';
}

interface FieldLike {
  field_id: string;
  data_source?: string | null;
  label?: string;
  /** The flat flag. Used only when no `required_rules` is configured. */
  required?: boolean;
  visibility_rules?: VisibilityRules | null;
  /** This form's own rule, overriding the shared one. See effectiveRules. */
  visibility_rules_override?: VisibilityRules | null;
  /**
   * Conditional requiredness — same condition shape, read as "required WHEN these hold".
   * Carried into the template snapshot from com_fields by the backend's
   * `_refresh_field_from_master`, so one place owns "is this mandatory, and when".
   */
  required_rules?: VisibilityRules | null;
}

interface SectionLike {
  fields?: FieldLike[] | null;
}

/**
 * Look up the value of a field referenced by a visibility_rules condition.
 * Conditions reference fields by their underlying `name` (from com_fields).
 * Imported form fields use `name` as their field_id
 * (UnifiedFieldService.convertToFormField), so the direct lookup hits in the
 * common case. Falls back to matching against data_source to cover fields that
 * were renamed but still point at the same source field.
 */
export function resolveDependencyValue(
  fieldRef: string,
  sections: SectionLike[] | null | undefined,
  value: Record<string, any> | undefined,
): any {
  if (!fieldRef || !value) return undefined;
  if (value[fieldRef] !== undefined) return value[fieldRef];
  for (const section of sections || []) {
    for (const f of section.fields || []) {
      const ds = f.data_source || '';
      const matches =
        ds === `member.custom_fields.${fieldRef}` || ds === `member.${fieldRef}`;
      if (matches && value[f.field_id] !== undefined) {
        return value[f.field_id];
      }
    }
  }
  return undefined;
}

/**
 * Normalize a dependency value into the flat list of candidate values the
 * operators below test against.
 *
 * Multi-select and checkbox fields hold a *list* of selected option values:
 * natively as an array in the form renderers, but as a JSON-encoded array
 * string on the member edit form (AddEditMemberNew persists them via
 * JSON.stringify). A scalar field (select / str / …) holds a plain string.
 * Comparing the raw stringified list against a single option value — e.g.
 * `String(['Other']) === 'Other'` is false, and `'["Other"]' === 'Other'` is
 * doubly false — is why a conditional keyed off a multi-select never fired
 * (TRUS-289). Collapsing every shape to string[] lets `equals`/`contains`/… mean
 * "is this option among the selected values", which is the intended semantics.
 */
export function toConditionValues(raw: any): string[] {
  if (raw === undefined || raw === null) return [''];
  if (Array.isArray(raw)) return raw.length ? raw.map((v) => String(v)) : [''];
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.length ? parsed.map((v) => String(v)) : [''];
        }
      } catch {
        /* not JSON — fall through and treat as a plain scalar string */
      }
    }
    return [raw];
  }
  return [String(raw)];
}

/**
 * Evaluate a single visibility-rule operator against a (possibly multi-valued)
 * dependency value. Shared by the form renderers (isFieldVisible below) and the
 * member edit form so a rule behaves identically on every surface.
 */
export function evaluateVisibilityOperator(
  operator: string,
  depValue: any,
  condValue: any,
): boolean {
  const values = toConditionValues(depValue);
  const target = String(condValue ?? '');

  switch (operator) {
    case 'is_populated':
      return values.some(
        (v) => v !== '' && v !== 'false' && v !== 'null' && v !== 'undefined',
      );
    case 'is_empty':
      return values.every((v) => v === '' || v === 'null' || v === 'undefined');
    case 'equals':
      return values.includes(target);
    case 'not_equals':
      return !values.includes(target);
    case 'contains':
      return values.some((v) => v.toLowerCase().includes(target.toLowerCase()));
    case 'in': {
      const allowed = target.split(',').map((s) => s.trim());
      return values.some((v) => allowed.includes(v));
    }
    default:
      return true;
  }
}

/**
 * Evaluate a field's visibility_rules against the current form `value`.
 * Returns true (visible) when there are no rules.
 */
/**
 * The rule a field actually renders by.
 *
 * A form's own `visibility_rules_override` beats the shared snapshot in
 * `visibility_rules`. Only `undefined`/`null` mean "no override, inherit" — an
 * override whose `conditions` list is EMPTY is a real answer meaning "always
 * show this field on this form", even where the shared rule would hide it.
 *
 * Hence `??` and not `||`. Both behave the same for an object, so this is not
 * guarding against a live bug; it states the rule the caller must not weaken
 * later, when `{}` or some other falsy-looking sentinel is tempting.
 *
 * The backend resolves the same precedence server-side, so this agrees with
 * what every other surface receives; doing it here too is what makes the
 * builder's live preview correct before the template has been saved.
 */
export function effectiveRules(field: FieldLike): VisibilityRules | null | undefined {
  return field.visibility_rules_override ?? field.visibility_rules;
}

function conditionsHold(
  rules: VisibilityRules | null | undefined,
  sections: SectionLike[] | null | undefined,
  value: Record<string, any> | undefined,
  fallback: boolean,
): boolean {
  if (!rules || !rules.conditions || rules.conditions.length === 0) return fallback;

  const results = rules.conditions.map((cond) => {
    const depValue = resolveDependencyValue(cond.field, sections, value);
    return evaluateVisibilityOperator(cond.operator, depValue, cond.value);
  });

  return rules.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

export function isFieldVisible(
  field: FieldLike,
  sections: SectionLike[] | null | undefined,
  value: Record<string, any> | undefined,
): boolean {
  // No rule means visible — visibility fails OPEN.
  return conditionsHold(effectiveRules(field), sections, value, true);
}

/**
 * Whether this field must carry a value before the form can be submitted.
 *
 * Mirrors `services/form_visibility.is_field_required` on the backend, which is the
 * authority — this exists so the user sees a field-level prompt instead of a 422 listing
 * everything at once after they press Submit.
 *
 * Two ways a field becomes required, in priority order:
 *   - `required_rules`: the same condition shape as `visibility_rules`, read as "required
 *     WHEN these hold". This is what makes requiredness depend on another answer — IFR's
 *     billing fields are mandatory only when Encounter Type is `Billable`, and a
 *     non-billable note must stay submittable without a procedure code (TRUS-566 #4).
 *   - the plain `required` flag, when no rule is configured — unchanged for every field
 *     that has no rule.
 *
 * A field the user cannot SEE is never required. Migration 647 hides IFR's billing fields
 * unless the encounter is Billable; without this, a required-but-hidden field produces a
 * form that cannot be submitted and cannot be fixed, because the offending field is not
 * on screen.
 *
 * Note the asymmetry with visibility: an absent/malformed rule here means NOT required
 * (fails closed — never invent a requirement nobody configured), where visibility fails
 * open. Same split as the backend.
 */
export function isFieldRequired(
  field: FieldLike,
  sections: SectionLike[] | null | undefined,
  value: Record<string, any> | undefined,
): boolean {
  if (!isFieldVisible(field, sections, value)) return false;
  const rules = field.required_rules;
  if (rules && rules.conditions && rules.conditions.length > 0) {
    return conditionsHold(rules, sections, value, false);
  }
  return Boolean(field.required);
}

/**
 * The answers with every value its own skip logic hid taken out, repeated until
 * nothing more drops — the client half of `strip_hidden_values(cascade=True)`.
 *
 * A rule tests one level ("Q24 shows when Q23 is Yes"), so it cannot also say
 * "and Q23 was itself shown". Feeding the result back in as the form's value
 * makes a hidden answer count as unanswered for every rule that reads it, so a
 * screened-out respondent cannot keep a later section in view (TRUS-624).
 * Only anonymous survey links use it; the input the person typed is untouched,
 * so a section that comes back into view still shows what they entered.
 */
export function cascadeVisibleValues(
  sections: SectionLike[] | null | undefined,
  value: Record<string, any>,
): Record<string, any> {
  let current = { ...value };
  for (;;) {
    const hidden: string[] = [];
    for (const section of sections || []) {
      for (const field of section.fields || []) {
        const id = field?.field_id;
        if (id && id in current && !isFieldVisible(field, sections, current)) hidden.push(id);
      }
    }
    if (hidden.length === 0) return current;
    current = { ...current };
    for (const id of hidden) delete current[id];
  }
}
